import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import Select from '../../../../components/ui/Select';
import { parseExcelPreview, bulkImportMatches, analyzeBulkImport } from '@/api/TournamentManagement/matchApi';
import { MatchFormat, MatchType, BulkImportRowAction, resolveEnum } from '@/models/tournament';
import type {
  BulkImportPreviewView,
  MatchBulkImportResultView,
  BulkImportRowResult,
  BulkImportAnalyzeResultView,
} from '@/models/tournament';

// ── Types ──────────────────────────────────────────────────────────────────

interface BulkImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  tournamentId: number;
  onSuccess: () => void;
}

type Phase = 'upload' | 'preview' | 'result';

// A row imports with Cancelled status when a STATUS column says so, or the venue
// cell carries a cancellation marker. Mirrors the backend's TryDetectCancelled.
const isRowCancelled = (row: { venue?: string; status?: string }): boolean =>
  `${row.status ?? ''} ${row.venue ?? ''}`.toUpperCase().includes('CANCEL');

// ── Component ──────────────────────────────────────────────────────────────

const BulkImportModal: React.FC<BulkImportModalProps> = ({
  isOpen,
  onClose,
  tournamentId,
  onSuccess,
}) => {
  const { t } = useTranslation('tournament');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Phase state
  const [phase, setPhase] = useState<Phase>('upload');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Upload phase
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Preview phase
  const [preview, setPreview] = useState<BulkImportPreviewView | null>(null);
  const [weekStartDate, setWeekStartDate] = useState('');
  const [defaultFormat, setDefaultFormat] = useState<string>(String(MatchFormat.ElevenAside));
  const [defaultType, setDefaultType] = useState<string>(String(MatchType.Outdoor));

  // Analyze (preview classification) state
  const [analysis, setAnalysis] = useState<BulkImportAnalyzeResultView | null>(null);
  const [analyzing, setAnalyzing] = useState(false);

  // Result phase
  const [result, setResult] = useState<MatchBulkImportResultView | null>(null);

  // Build a quick lookup so we can render an action badge per row
  const resultsByRow = useMemo(() => {
    const map = new Map<number, BulkImportRowResult>();
    if (analysis) {
      for (const r of analysis.rowResults) map.set(r.rowIndex, r);
    }
    return map;
  }, [analysis]);

  // Re-analyze when settings change in preview phase (debounced)
  useEffect(() => {
    if (phase !== 'preview' || !preview || !weekStartDate) {
      setAnalysis(null);
      return;
    }
    let cancelled = false;
    setAnalyzing(true);
    const handle = setTimeout(async () => {
      try {
        const response = await analyzeBulkImport({
          input: {
            tournamentId,
            weekStartDate,
            defaultFormat: parseInt(defaultFormat, 10) as MatchFormat,
            defaultType: parseInt(defaultType, 10) as MatchType,
          },
          rows: preview.rows,
        });
        if (cancelled) return;
        if (response.errors.length === 0 && response.payload) {
          setAnalysis(response.payload);
        }
      } catch (err) {
        if (!cancelled) console.error('Analyze failed:', err);
      } finally {
        if (!cancelled) setAnalyzing(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [phase, preview, weekStartDate, defaultFormat, defaultType, tournamentId]);

  // Rows that imported (or will import) carrying one or more non-blocking warnings.
  const warnedRows = useMemo(
    () => (result?.rowResults ?? []).filter((r) => r.warnings && r.warnings.length > 0),
    [result]
  );

  const hasReferees = useMemo(() => {
    if (!preview) return { headRef: false, ar1: false, ar2: false, fourth: false };
    return {
      headRef: preview.rows.some((r) => !!r.headReferee),
      ar1: preview.rows.some((r) => !!r.assistantReferee1),
      ar2: preview.rows.some((r) => !!r.assistantReferee2),
      fourth: preview.rows.some((r) => !!r.fourthOfficial),
    };
  }, [preview]);

  // Drag-and-drop visual state
  const [isDragging, setIsDragging] = useState(false);

  // ── Handlers ──

  const isExcelFile = (file: File) => {
    const name = file.name.toLowerCase();
    return name.endsWith('.xlsx') || name.endsWith('.xls');
  };

  const processFile = async (file: File) => {
    if (!isExcelFile(file)) {
      setError(t('Only .xlsx and .xls files are supported'));
      return;
    }

    setSelectedFile(file);
    setError('');
    setIsLoading(true);

    try {
      const response = await parseExcelPreview(file);
      if (response.errors.length > 0) {
        setError(response.errors[0]?.description || t('Failed to parse Excel file'));
        setIsLoading(false);
        return;
      }
      if (response.payload) {
        setPreview(response.payload);
        setPhase('preview');
      }
    } catch (error: any) {
      const msg = error?.response?.data?.errors?.[0]?.description
        || error?.message
        || t('Something went wrong. Please try again.');
      setError(msg);
      console.error('Error parsing Excel file:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await processFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (isLoading) return;
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    await processFile(file);
  };

  const handleImport = async () => {
    if (!preview || !weekStartDate) {
      setError(t('Please fill in all required fields'));
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      const response = await bulkImportMatches({
        input: {
          tournamentId,
          weekStartDate,
          defaultFormat: parseInt(defaultFormat, 10) as MatchFormat,
          defaultType: parseInt(defaultType, 10) as MatchType,
        },
        rows: preview.rows,
      });

      if (response.errors.length > 0) {
        setError(response.errors[0]?.description || t('Failed to import matches'));
        setIsLoading(false);
        return;
      }

      if (response.payload) {
        setResult(response.payload);
        setPhase('result');
      }
    } catch (error: any) {
      const msg = error?.response?.data?.errors?.[0]?.description
        || error?.message
        || t('Something went wrong. Please try again.');
      setError(msg);
      console.error('Error importing matches:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    // Reset all state
    setPhase('upload');
    setIsLoading(false);
    setError('');
    setSelectedFile(null);
    setPreview(null);
    setWeekStartDate('');
    setDefaultFormat(String(MatchFormat.FiveAside));
    setDefaultType(String(MatchType.Outdoor));
    setResult(null);
    setAnalysis(null);
    setAnalyzing(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const handleDone = () => {
    onSuccess();
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      data-testid="bulk-import-modal"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-import-modal-title"
        className="bg-card rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col"
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <h2
            id="bulk-import-modal-title"
            className="text-lg font-semibold text-foreground flex items-center gap-2"
          >
            <Icon name="Upload" size={20} className="text-primary" />
            {t('Import Matches from Excel')}
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors focus-visible:ring-2 focus-visible:ring-primary/20"
            data-testid="bulk-import-close-btn"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="overflow-y-auto flex-1 p-6 space-y-5">

          {/* Phase 1: Upload */}
          {phase === 'upload' && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t('Upload an Excel file (.xlsx) containing match data. Expected columns: Day, Venue, Pitch, Time, Age Category, Division, Team 1, Team 2, Status, Commissioner. Rows with a missing day, time, or team still import and are flagged with a warning.')}
              </p>

              <div
                role="button"
                tabIndex={0}
                aria-label={t('Drop an Excel file here or click to browse')}
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer focus-visible:ring-2 focus-visible:ring-primary/20 ${
                  isDragging
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
                onDragOver={handleDragOver}
                onDragEnter={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                data-testid="bulk-import-dropzone"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="bulk-import-file-input"
                />
                {isLoading ? (
                  <div className="flex flex-col items-center gap-3">
                    <Icon name="Loader2" size={40} className="text-primary animate-spin" />
                    <p className="text-sm font-medium text-foreground">{t('Parsing file...')}</p>
                  </div>
                ) : isDragging ? (
                  <div className="flex flex-col items-center gap-3">
                    <Icon name="Download" size={40} className="text-primary" />
                    <p className="text-sm font-medium text-foreground">{t('Drop the file to upload')}</p>
                    <p className="text-xs text-muted-foreground">.xlsx, .xls</p>
                  </div>
                ) : selectedFile ? (
                  <div className="flex flex-col items-center gap-3">
                    <Icon name="FileSpreadsheet" size={40} className="text-primary" />
                    <p className="text-sm font-medium text-foreground">{selectedFile.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {t('Drop a new file or click to browse')}
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <Icon name="Upload" size={40} className="text-muted-foreground" />
                    <p className="text-sm font-medium text-foreground">
                      {t('Drag and drop an Excel file here, or click to browse')}
                    </p>
                    <p className="text-xs text-muted-foreground">.xlsx, .xls</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Phase 2: Preview & Configure */}
          {phase === 'preview' && preview && (
            <div className="space-y-5">
              {/* Summary */}
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <span className="flex items-center gap-1.5 text-foreground">
                  <Icon name="FileSpreadsheet" size={16} className="text-primary" />
                  {t('{{count}} rows parsed', { count: preview.totalRows })}
                </span>
                {analysis ? (
                  <>
                    <span className="flex items-center gap-1.5 text-green-600">
                      <Icon name="PlusCircle" size={16} />
                      {t('{{count}} new', { count: analysis.newCount })}
                    </span>
                    <span className="flex items-center gap-1.5 text-blue-600">
                      <Icon name="RefreshCw" size={16} />
                      {t('{{count}} update', { count: analysis.updateCount })}
                    </span>
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                      <Icon name="MinusCircle" size={16} />
                      {t('{{count}} skip', { count: analysis.skipCount })}
                    </span>
                    {analysis.warningCount > 0 && (
                      <span className="flex items-center gap-1.5 text-amber-600">
                        <Icon name="AlertTriangle" size={16} />
                        {t('{{count}} with warnings', { count: analysis.warningCount })}
                      </span>
                    )}
                    {analysis.errorCount > 0 && (
                      <span className="flex items-center gap-1.5 text-destructive">
                        <Icon name="AlertCircle" size={16} />
                        {t('{{count}} error', { count: analysis.errorCount })}
                      </span>
                    )}
                  </>
                ) : analyzing ? (
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <Icon name="Loader2" size={16} className="animate-spin" />
                    {t('Analyzing rows...')}
                  </span>
                ) : !weekStartDate ? (
                  <span className="flex items-center gap-1.5 text-amber-600">
                    <Icon name="Info" size={16} />
                    {t('Pick a week start date to see new vs update breakdown')}
                  </span>
                ) : null}
              </div>

              {/* Warnings */}
              {preview.warnings.length > 0 && (
                <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <Icon name="AlertTriangle" size={16} className="text-amber-600 shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                        {t('Warnings')}
                      </p>
                      <ul className="text-xs text-amber-700 dark:text-amber-300 space-y-0.5">
                        {preview.warnings.map((w, i) => (
                          <li key={i}>{w}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Preview table */}
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="max-h-96 overflow-auto">
                  <table className="w-full text-sm" data-testid="bulk-import-preview-table">
                    <thead className="bg-muted/50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">#</th>
                        <th className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">{t('Status')}</th>
                        <th className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">{t('Day')}</th>
                        <th className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">{t('Time')}</th>
                        <th className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">{t('Venue')}</th>
                        <th className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">{t('Pitch')}</th>
                        <th className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">{t('Age Category')}</th>
                        <th className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">{t('Division')}</th>
                        <th className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">{t('Team 1')}</th>
                        <th className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">{t('Team 2')}</th>
                        <th className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">{t('Commissioner')}</th>
                        {hasReferees.headRef && (
                          <th className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">{t('Head Referee')}</th>
                        )}
                        {hasReferees.ar1 && (
                          <th className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">{t('Assistant Referee 1')}</th>
                        )}
                        {hasReferees.ar2 && (
                          <th className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">{t('Assistant Referee 2')}</th>
                        )}
                        {hasReferees.fourth && (
                          <th className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">{t('4th Official')}</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {preview.rows.map((row, i) => {
                        const rowResult = resultsByRow.get(i + 1);
                        return (
                          <tr key={i} className="hover:bg-muted/30">
                            <td className="px-3 py-2 text-muted-foreground">{i + 1}</td>
                            <td className="px-3 py-2">
                              <div className="flex flex-wrap items-center gap-1">
                                <ActionBadge result={rowResult} t={t} />
                                {isRowCancelled(row) && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">
                                    <Icon name="Ban" size={11} />
                                    {t('Cancelled')}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-2">{row.day}</td>
                            <td className="px-3 py-2">
                              {row.time
                                ? row.time
                                : <span className="text-muted-foreground italic">{t('Time TBD')}</span>}
                            </td>
                            <td className="px-3 py-2">{row.venue}</td>
                            <td className="px-3 py-2">{row.pitch || '-'}</td>
                            <td className="px-3 py-2">{row.ageCategory || '-'}</td>
                            <td className="px-3 py-2">{row.division || '-'}</td>
                            <td className="px-3 py-2 font-medium">{row.team1Name}</td>
                            <td className="px-3 py-2 font-medium">{row.team2Name}</td>
                            <td className="px-3 py-2">{row.commissioner || '-'}</td>
                            {hasReferees.headRef && (
                              <td className="px-3 py-2">{row.headReferee || '-'}</td>
                            )}
                            {hasReferees.ar1 && (
                              <td className="px-3 py-2">{row.assistantReferee1 || '-'}</td>
                            )}
                            {hasReferees.ar2 && (
                              <td className="px-3 py-2">{row.assistantReferee2 || '-'}</td>
                            )}
                            {hasReferees.fourth && (
                              <td className="px-3 py-2">{row.fourthOfficial || '-'}</td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Configuration */}
              <div className="space-y-3 border-t border-border pt-4">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Icon name="Settings" size={16} className="text-primary" />
                  {t('Import Settings')}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1">
                      {t('Week Start Date')} *
                    </label>
                    <Input
                      type="date"
                      value={weekStartDate}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWeekStartDate(e.target.value)}
                      data-testid="bulk-import-week-start-date"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1">
                      {t('Default Format')}
                    </label>
                    <Select
                      value={defaultFormat}
                      onChange={(v: string) => setDefaultFormat(v)}
                      options={[
                        { value: String(MatchFormat.FiveAside), label: t('5 aside') },
                        { value: String(MatchFormat.SevenAside), label: t('7 aside') },
                        { value: String(MatchFormat.NineAside), label: t('9 aside') },
                        { value: String(MatchFormat.ElevenAside), label: t('11 aside') },
                      ]}
                      data-testid="bulk-import-default-format"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1">
                      {t('Default Type')}
                    </label>
                    <Select
                      value={defaultType}
                      onChange={(v: string) => setDefaultType(v)}
                      options={[
                        { value: String(MatchType.Outdoor), label: t('Outdoor') },
                        { value: String(MatchType.Indoor), label: t('Indoor') },
                      ]}
                      data-testid="bulk-import-default-type"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Phase 3: Result */}
          {phase === 'result' && result && (
            <div className="space-y-4">
              {/* Success summary */}
              <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <Icon name="CheckCircle" size={24} className="text-green-600 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-green-800 dark:text-green-200">
                      {t('Import Complete')}
                    </p>
                    <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                      {t('Created {{count}} matches', { count: result.created })}
                      {result.updated > 0 && ` · ${t('{{count}} updated', { count: result.updated })}`}
                      {result.skipped > 0 && ` · ${t('{{count}} skipped', { count: result.skipped })}`}
                      {result.warningCount > 0 && ` · ${t('{{count}} with warnings', { count: result.warningCount })}`}
                    </p>
                  </div>
                </div>
              </div>

              {/* Warnings — rows that imported but need a look */}
              {warnedRows.length > 0 && (
                <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <Icon name="AlertTriangle" size={16} className="text-amber-600 shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                        {t('{{count}} imported with warnings', { count: warnedRows.length })}
                      </p>
                      <ul className="text-xs text-amber-700 dark:text-amber-300 space-y-1 max-h-48 overflow-auto">
                        {warnedRows.map((r) => (
                          <li key={r.rowIndex}>
                            <span className="font-medium">{t('Row {{n}}', { n: r.rowIndex })}:</span>{' '}
                            {r.warnings.join('; ')}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Errors */}
              {result.errors.length > 0 && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <Icon name="AlertCircle" size={16} className="text-destructive shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-destructive">
                        {t('{{count}} errors occurred', { count: result.errors.length })}
                      </p>
                      <ul className="text-xs text-destructive/80 space-y-0.5 max-h-40 overflow-auto">
                        {result.errors.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Error display */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 flex items-center gap-2">
              <Icon name="AlertCircle" size={16} className="text-destructive shrink-0" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border shrink-0">
          {phase === 'upload' && (
            <Button
              type="button"
              onClick={handleClose}
              className="px-5 py-2 border border-border text-foreground rounded-lg hover:bg-muted"
              data-testid="bulk-import-cancel-btn"
            >
              {t('Cancel')}
            </Button>
          )}

          {phase === 'preview' && (
            <>
              <Button
                type="button"
                onClick={() => {
                  setPhase('upload');
                  setPreview(null);
                  setSelectedFile(null);
                  setError('');
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className="px-5 py-2 border border-border text-foreground rounded-lg hover:bg-muted"
                data-testid="bulk-import-back-btn"
              >
                {t('Back')}
              </Button>
              <Button
                type="button"
                onClick={handleImport}
                disabled={isLoading || !weekStartDate}
                className="px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="bulk-import-confirm-btn"
              >
                <Icon
                  name={isLoading ? 'Loader2' : 'Upload'}
                  size={16}
                  className={isLoading ? 'animate-spin' : ''}
                />
                {isLoading
                  ? t('Importing...')
                  : analysis
                    ? t('Apply ({{n}} new, {{u}} update)', { n: analysis.newCount, u: analysis.updateCount })
                    : t('Import {{count}} Matches', { count: preview?.validRows ?? 0 })}
              </Button>
            </>
          )}

          {phase === 'result' && (
            <Button
              type="button"
              onClick={handleDone}
              className="px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2"
              data-testid="bulk-import-done-btn"
            >
              <Icon name="Check" size={16} />
              {t('Done')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Action Badge ───────────────────────────────────────────────────────────

const ActionBadge: React.FC<{
  result: BulkImportRowResult | undefined;
  t: (key: string) => string;
}> = ({ result, t }) => {
  if (!result) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-muted text-muted-foreground">
        {t('—')}
      </span>
    );
  }
  // The backend serializes enums by name ("New", "Update", …); resolveEnum
  // normalizes either form to the numeric member so the switch matches.
  const action = resolveEnum(BulkImportRowAction, result.action);
  const cfg = (() => {
    switch (action) {
      case BulkImportRowAction.New:
        return { label: t('New'), className: 'bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-300' };
      case BulkImportRowAction.Update:
        return { label: t('Update'), className: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300' };
      case BulkImportRowAction.Skip:
        return { label: t('Skip'), className: 'bg-muted text-muted-foreground' };
      case BulkImportRowAction.Error:
      default:
        return { label: t('Error'), className: 'bg-destructive/10 text-destructive' };
    }
  })();
  const warnings = result.warnings ?? [];
  const badgeTooltip = [result.message, ...warnings].filter(Boolean).join('\n') || undefined;
  return (
    <span className="inline-flex items-center gap-1">
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${cfg.className}`}
        title={badgeTooltip}
      >
        {cfg.label}
      </span>
      {warnings.length > 0 && (
        <span
          className="inline-flex items-center text-amber-600"
          title={warnings.join('\n')}
          aria-label={t('Has warnings')}
        >
          <Icon name="AlertTriangle" size={12} />
        </span>
      )}
    </span>
  );
};

export default BulkImportModal;
